import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { API_DOMAIN } from "config";

const DEFAULT_IMG = "/images/img_car11_qgcqjcnfp_428x738.png";
const LOCAL_PREFIXES = ["/images/", "/assets/", "/static/"];

function isAbsoluteUrl(url) {
  return /^https?:\/\//i.test(url) || url.startsWith("data:") || url.startsWith("blob:");
}

export default function ImageCarousel({ images = [] }) {
  const [index, setIndex] = useState(0); // לקרוסלה בדף
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0); // ללייטבוקס
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [rotation, setRotation] = useState(0);
  const requestVersion = useMemo(() => Date.now(), [images]);

  const hasImages = Array.isArray(images) && images.length > 0;
  const canNavigate = hasImages && images.length > 1;

  const getImgUrl = useCallback((img) => {
    if (!img?.image_url) return DEFAULT_IMG;

    const url = String(img.image_url).trim();
    if (!url) return DEFAULT_IMG;

    const withVersion = (src) => {
      if (!src || src === DEFAULT_IMG || src.startsWith("data:") || src.startsWith("blob:")) {
        return src;
      }
      const separator = src.includes("?") ? "&" : "?";
      return `${src}${separator}v=${requestVersion}`;
    };

    // URL מלא / data / blob
    if (isAbsoluteUrl(url)) return withVersion(url);

    // נכסים של הפרונט
    if (LOCAL_PREFIXES.some((p) => url.startsWith(p))) return withVersion(url);

    const base = String(API_DOMAIN).replace(/\/+$/, "");

    // /uploads/... וכו'
    if (url.startsWith("/")) return withVersion(`${base}${url}`);

    return withVersion(`${base}/${url}`);
  }, [requestVersion]);

  // אם התמונות משתנות/מצטמצמות – לא להשאיר אינדקס מחוץ לטווח
  useEffect(() => {
    if (!hasImages) return;
    setIndex((i) => Math.min(i, images.length - 1));
    setModalIndex((i) => Math.min(i, images.length - 1));
  }, [hasImages, images.length]);

  // reset fade + rotation
  useEffect(() => {
    setIsImageLoaded(false);
    setRotation(0);
  }, [modalIndex, isModalOpen]);

  const prev = () => setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  const openModal = (i = 0) => {
    setModalIndex(i);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const rotateImage = (e) => {
    e?.stopPropagation?.();
    setRotation((r) => (r + 90) % 360);
  };

  const modalPrev = (e) => {
    e?.stopPropagation?.();
    setModalIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  };

  const modalNext = (e) => {
    e?.stopPropagation?.();
    setModalIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  };

  const currentImageUrl = useMemo(() => {
    if (!hasImages) return DEFAULT_IMG;
    return getImgUrl(images?.[modalIndex]);
  }, [hasImages, images, modalIndex, getImgUrl]);

  // ✅ לתצוגה בכרטיס: רק תמונה אחת
  const cardImageUrl = useMemo(() => {
    if (!hasImages) return DEFAULT_IMG;
    return getImgUrl(images?.[index]);
  }, [hasImages, images, index, getImgUrl]);

  const handleImgError = (e) => {
    if (!e?.currentTarget) return;
    e.currentTarget.src = DEFAULT_IMG;
  };

  // ========= לייטבוקס (ic-*) =========
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const lastActiveElRef = useRef(null);

  useEffect(() => {
    if (!isModalOpen) return;

    lastActiveElRef.current = document.activeElement;
    setTimeout(() => closeBtnRef.current?.focus?.(), 0);

    const handleKey = (e) => {
      if (e.key === "ArrowRight") setModalIndex((i) => (i === images.length - 1 ? 0 : i + 1));
      if (e.key === "ArrowLeft") setModalIndex((i) => (i === 0 ? images.length - 1 : i - 1));
      if (e.key === "Escape") setIsModalOpen(false);
      if (e.key.toLowerCase() === "r") setRotation((r) => (r + 90) % 360);

      // Focus trap
      if (e.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;

        const focusables = root.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const list = Array.from(focusables).filter((el) => !el.hasAttribute("disabled"));
        if (list.length === 0) return;

        const first = list[0];
        const last = list[list.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
      lastActiveElRef.current?.focus?.();
    };
  }, [isModalOpen, images.length]);

  // חסימת גלילה ברקע
  useEffect(() => {
    if (!isModalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow || "";
    };
  }, [isModalOpen]);

  // preload next/prev
  const preloadTimerRef = useRef(null);

  useEffect(() => {
    if (!isModalOpen || !hasImages) return;

    if (preloadTimerRef.current) clearTimeout(preloadTimerRef.current);

    preloadTimerRef.current = setTimeout(() => {
      const preload = (i) => {
        const img = images[i];
        if (!img) return;
        const src = getImgUrl(img);
        if (!src) return;
        const im = new Image();
        im.decoding = "async";
        im.src = src;
      };

      const nextIdx = modalIndex === images.length - 1 ? 0 : modalIndex + 1;
      const prevIdx = modalIndex === 0 ? images.length - 1 : modalIndex - 1;
      preload(nextIdx);
      preload(prevIdx);
    }, 120);

    return () => {
      if (preloadTimerRef.current) clearTimeout(preloadTimerRef.current);
    };
  }, [isModalOpen, modalIndex, images, hasImages, getImgUrl]);

  // swipe במובייל
  const touchRef = useRef({ x: 0, y: 0, t: 0 });

  const onTouchStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    touchRef.current = { x: touch.clientX, y: touch.clientY, t: Date.now() };
  };

  const onTouchEnd = (e) => {
    const touch = e.changedTouches?.[0];
    if (!touch) return;

    const dx = touch.clientX - touchRef.current.x;
    const dy = touch.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;

    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (dt > 700) return;
    if (absX < 50) return;
    if (absY > 80) return;

    if (dx < 0) modalNext();
    else modalPrev();
  };

  const stop = (e) => e.stopPropagation();

  if (!hasImages) return null;

  return (
    <>
      <div
       className="ic-card 0  relative overflow-hidden rounded-[16px] h-[425px]  wrap-main-img"
       style={{backgroundColor: "none"}}
       
       >
        <img
          src={cardImageUrl}
          alt={`slide-${index}`}
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => openModal(index)}
          draggable={false}
          loading={index === 0 ? "eager" : "lazy"}
          decoding="async"
          fetchpriority={index === 0 ? "high" : "auto"}
          onError={handleImgError}
style={{
  borderBottomLeftRadius: '10px',
  borderBottomRightRadius: '10px'
}}        />

        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="ic-card-arrow ic-card-arrow--left"
              type="button"
              aria-label="הקודם"
            >
              ‹
            </button>

            <button
              onClick={next}
              className="ic-card-arrow ic-card-arrow--right"
              type="button"
              aria-label="הבא"
            >
              ›
            </button>
          </>
        )}
      </div>

      {isModalOpen && (
        <div className="ic-overlay" role="dialog" aria-modal="true" aria-label="תצוגת תמונה" onClick={closeModal}>
          <div
            ref={dialogRef}
            className="ic-dialog"
            onClick={stop}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="ic-header">
              <div className="ic-header-left">
                <button
                  type="button"
                  aria-label="סגור"
                  ref={closeBtnRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                  className="ic-close"
                >
                  ×
                </button>
              </div>

              <div className="ic-counter">
                {modalIndex + 1} / {images.length}
              </div>

              <div className="ic-header-spacer" aria-hidden="true" />
            </div>

            <div className="ic-center">
              <div className="ic-stage">
                {canNavigate && (
                  <>
                    <button onClick={modalPrev} type="button" aria-label="הקודם" className="ic-lb-arrow ic-lb-arrow--left">
                      ❮
                    </button>
                    <button onClick={modalNext} type="button" aria-label="הבא" className="ic-lb-arrow ic-lb-arrow--right">
                      ❯
                    </button>
                  </>
                )}

                <button
                  type="button"
                  aria-label="סובב תמונה"
                  onClick={rotateImage}
                  className="ic-rotate-floating"
                >
                  ↻
                </button>

                {!isImageLoaded && <div className="ic-loader" />}

                <img
                  src={currentImageUrl}
                  alt={`modal-slide-${modalIndex}`}
                  onLoad={() => setIsImageLoaded(true)}
                  onError={handleImgError}
                  className={`ic-main-img ic-fade ${isImageLoaded ? "ic-fade--show" : "ic-fade--hide"}`}
                  style={{ transform: `rotate(${rotation}deg)` }}
                  draggable={false}
                  decoding="async"
                  fetchpriority="high"
                />

                {images.length > 1 && (
                  <div className="ic-thumbs-wrap">
                    <div className="ic-thumbs">
                      {images.map((img, i) => {
                        const thumbUrl = getImgUrl(img);
                        const isActive = i === modalIndex;

                        return (
                          <button key={i} type="button" onClick={() => setModalIndex(i)} className="ic-thumb-btn">
                            <div className={`ic-thumb-box ${isActive ? "ic-thumb-box--active" : ""}`}>
                              <img
                                src={thumbUrl}
                                alt={`thumb-${i}`}
                                draggable={false}
                                className="ic-thumb-img"
                                loading="lazy"
                                decoding="async"
                                onError={handleImgError}
                              />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ic-backdrop-click" onClick={closeModal} />
        </div>
      )}
    </>
  );
}
